/**
 * Word Template Engine — Direct Rendering with Image Injection
 *
 * Renders the template as a complete document via docxtemplater. The template's
 * own structure drives the output: cover page placeholders ({TITLE}, {DATE}, etc.)
 * are filled, and the {#artefacts}...{/artefacts} loop repeats once per artefact
 * with {title} and {content} tags.
 *
 * HTML artefacts (charts, visualisations) are captured as images externally and
 * injected into the rendered document as inline OOXML drawings. A unique marker
 * is placed during rendering, then replaced post-render with the image XML +
 * binary data. This avoids the paid docxtemplater Image Module.
 *
 * This avoids the fragile XML-merge approach (extracting cover-page XML from one
 * ZIP and injecting it into another) which caused document corruption — especially
 * with .dotx templates that use different OOXML content types and relationship
 * structures. By letting docxtemplater own the entire render, headers, footers,
 * media, and relationships all stay internally consistent.
 */

import type { Artefact } from "@/types/artefacts";
import type { CapturedImage } from "@/utils/artefactExport/htmlToImage";

import Docxtemplater from "docxtemplater";
import PizZip from "pizzip";

import { ArtefactType } from "@/types/artefacts";
import { parseTableContent } from "@/utils/tableUtils";
import { logger } from "@/telemetry";

import { stripMarkdown } from "./templateUtils";

/** Marker prefix for HTML artefacts that should become images post-render. */
const IMG_PLACEHOLDER_PREFIX = "__ARTEFACT_IMG_";
/** Marker prefix for table artefacts that should become OOXML tables post-render. */
const TBL_PLACEHOLDER_PREFIX = "__ARTEFACT_TBL_";
/** Marker for the {TOC} placeholder — replaced with a Word TOC field code. */
const TOC_PLACEHOLDER = "__TOC__";

/** Pixels → EMU at 96 DPI (1 inch = 914 400 EMU). */
const PX_TO_EMU = 914400 / 96;
/** Max inline image width: ~15 cm to fit A4 with margins. */
const MAX_IMG_WIDTH_EMU = 5486400;
/** Max inline image height: ~20 cm. */
const MAX_IMG_HEIGHT_EMU = 7315200;

export interface DocxTemplateData {
  title?: string;
  date?: string;
  author?: string;
  brand?: string;
  artefacts: Artefact[];
}

/**
 * Prepare template data from artefacts and options.
 */
export function prepareTemplateData(
  artefacts: Artefact[],
  options: { title?: string; author?: string; brand?: string; locale?: string } = {},
): DocxTemplateData {
  return {
    title: options.title || "",
    date: new Date().toLocaleDateString(options.locale || navigator.language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    author: options.author || "",
    brand: options.brand || "",
    artefacts,
  };
}

/**
 * Render a Word template with cover-page metadata and artefact content.
 *
 * Text artefacts have markdown stripped so they render as clean plain text
 * styled by the template's paragraph formatting. HTML artefacts are injected
 * as inline images when an imageMap is provided.
 *
 * @param templateBuffer - The .docx/.dotx template as ArrayBuffer
 * @param data - Metadata and artefacts for template rendering
 * @param imageMap - Optional map of artefact ID → captured image for HTML artefacts
 * @returns Rendered .docx blob
 */
export async function applyWordTemplate(
  templateBuffer: ArrayBuffer,
  data: DocxTemplateData,
  imageMap?: Map<string, CapturedImage>,
): Promise<Blob> {
  try {
    const templateZip = new PizZip(templateBuffer);
    const doc = new Docxtemplater(templateZip, {
      nullGetter: () => "",
      paragraphLoop: true,
      linebreaks: true,
    });

    doc.render({
      TITLE: data.title,
      DATE: data.date,
      AUTHOR: data.author,
      BRAND: data.brand,
      TOC: TOC_PLACEHOLDER,
      title: data.title,
      date: data.date,
      author: data.author,
      brand: data.brand,
      artefacts: data.artefacts.map((a) => ({
        title: a.title,
        content: resolveArtefactContent(a, imageMap),
      })),
    });

    const zip = doc.getZip();

    // .dotx templates declare their main part as "template.main+xml" in
    // [Content_Types].xml. When we save as .docx this must be "document.main+xml",
    // otherwise Word sees a content-type mismatch and reports corruption.
    patchDotxContentType(zip);

    // Inject captured images and native tables, replacing placeholder markers
    injectRichContent(zip, data.artefacts, imageMap);

    const output = zip.generate({
      type: "blob",
      mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    logger.info("Word template rendered", {
      artefactCount: data.artefacts.length,
      imagesInjected: imageMap?.size ?? 0,
      title: data.title,
    });

    return output;
  } catch (error) {
    logger.error("Failed to apply Word template", { error: String(error) });
    throw new Error(
      `Failed to apply Word template: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Decide how an artefact's content should appear in the docxtemplater render.
 *
 * - HTML artefacts with a captured image → image placeholder marker
 * - TABLE artefacts → table placeholder marker (native OOXML table injected post-render)
 * - Everything else → markdown-stripped plain text
 */
function resolveArtefactContent(artefact: Artefact, imageMap?: Map<string, CapturedImage>): string {
  if (imageMap?.has(artefact.id)) {
    return `${IMG_PLACEHOLDER_PREFIX}${artefact.id}__`;
  }

  if (artefact.type === ArtefactType.TABLE) {
    return `${TBL_PLACEHOLDER_PREFIX}${artefact.id}__`;
  }

  return stripMarkdown(artefact.content);
}

/** Rewrite .dotx content type to .docx so Word opens the file correctly. */
function patchDotxContentType(zip: PizZip): void {
  const file = zip.file("[Content_Types].xml");

  if (!file) return;

  const ct = file.asText();
  const patched = ct.replace(
    /wordprocessingml\.template\.main\+xml/g,
    "wordprocessingml.document.main+xml",
  );

  if (patched !== ct) {
    zip.file("[Content_Types].xml", patched);
  }
}

/**
 * Post-render injection of rich content: images for HTML artefacts and
 * native OOXML tables for TABLE artefacts. Placeholder markers left by
 * `resolveArtefactContent` are located in the rendered XML and replaced.
 */
function injectRichContent(
  zip: PizZip,
  artefacts: Artefact[],
  imageMap?: Map<string, CapturedImage>,
): void {
  let docXml = zip.file("word/document.xml")?.asText();

  if (!docXml) return;

  let relsXml = zip.file("word/_rels/document.xml.rels")?.asText();
  let newRels = "";
  let nextRId = 1;
  let docPrId = 1000;
  let pngAdded = false;
  let modified = false;

  if (relsXml) {
    const existingRIds = [...relsXml.matchAll(/Id="rId(\d+)"/g)].map((m) => parseInt(m[1]));

    nextRId = Math.max(0, ...existingRIds) + 1;
  }

  for (const artefact of artefacts) {
    // --- Image injection (HTML artefacts) ---
    const image = imageMap?.get(artefact.id);

    if (image) {
      const placeholder = `${IMG_PLACEHOLDER_PREFIX}${artefact.id}__`;
      const idx = docXml.indexOf(placeholder);

      if (idx !== -1) {
        let widthEmu = Math.round(image.width * PX_TO_EMU);
        let heightEmu = Math.round(image.height * PX_TO_EMU);

        if (widthEmu > MAX_IMG_WIDTH_EMU) {
          heightEmu = Math.round(heightEmu * (MAX_IMG_WIDTH_EMU / widthEmu));
          widthEmu = MAX_IMG_WIDTH_EMU;
        }

        if (heightEmu > MAX_IMG_HEIGHT_EMU) {
          widthEmu = Math.round(widthEmu * (MAX_IMG_HEIGHT_EMU / heightEmu));
          heightEmu = MAX_IMG_HEIGHT_EMU;
        }

        const rId = `rId${nextRId++}`;

        docPrId++;
        const mediaFilename = `artefact_${artefact.id}.png`;

        zip.file(`word/media/${mediaFilename}`, image.imageData);
        newRels +=
          `<Relationship Id="${rId}" ` +
          `Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" ` +
          `Target="media/${mediaFilename}"/>`;

        const imageParagraph = buildImageParagraphXml(rId, widthEmu, heightEmu, docPrId);

        docXml = replacePlaceholderWithBlock(docXml, placeholder, idx, imageParagraph);
        pngAdded = true;
        modified = true;
      }

      continue;
    }

    // --- Table injection (TABLE artefacts) ---
    if (artefact.type === ArtefactType.TABLE) {
      const placeholder = `${TBL_PLACEHOLDER_PREFIX}${artefact.id}__`;
      const idx = docXml.indexOf(placeholder);

      if (idx !== -1) {
        const tableData = parseTableContent(artefact.content);

        if (tableData && tableData.headers.length > 0) {
          const tableXml = buildTableXml(tableData.headers, tableData.rows);

          docXml = replacePlaceholderWithBlock(docXml, placeholder, idx, tableXml);
          modified = true;
        }
      }
    }
  }

  // --- TOC field code injection ---
  const tocIdx = docXml.indexOf(TOC_PLACEHOLDER);

  if (tocIdx !== -1) {
    const tocFieldXml = buildTocFieldXml();

    docXml = replacePlaceholderWithBlock(docXml, TOC_PLACEHOLDER, tocIdx, tocFieldXml);
    modified = true;
  }

  if (!modified) return;

  zip.file("word/document.xml", docXml);

  if (newRels && relsXml) {
    relsXml = relsXml.replace("</Relationships>", `${newRels}</Relationships>`);
    zip.file("word/_rels/document.xml.rels", relsXml);
  }

  if (pngAdded) {
    let ctXml = zip.file("[Content_Types].xml")?.asText();

    if (ctXml && !ctXml.includes('Extension="png"')) {
      ctXml = ctXml.replace(
        "</Types>",
        '<Default Extension="png" ContentType="image/png"/></Types>',
      );
      zip.file("[Content_Types].xml", ctXml);
    }
  }
}

/**
 * Remove a placeholder string from the document XML and insert a block-level
 * XML fragment (paragraph, table, etc.) after the containing `</w:p>`.
 */
function replacePlaceholderWithBlock(
  docXml: string,
  placeholder: string,
  placeholderIdx: number,
  blockXml: string,
): string {
  const pCloseTag = "</w:p>";
  const pCloseIdx: number = docXml.indexOf(pCloseTag, placeholderIdx);

  if (pCloseIdx === -1) return docXml;

  const pCloseEnd: number = pCloseIdx + pCloseTag.length;

  return (
    docXml.substring(0, placeholderIdx) +
    docXml.substring(placeholderIdx + placeholder.length, pCloseEnd) +
    blockXml +
    docXml.substring(pCloseEnd)
  );
}

/** Build OOXML for a standalone paragraph containing an inline image. */
function buildImageParagraphXml(
  rId: string,
  widthEmu: number,
  heightEmu: number,
  docPrId: number,
): string {
  return (
    "<w:p>" +
    "<w:r>" +
    "<w:drawing>" +
    '<wp:inline distT="0" distB="0" distL="0" distR="0">' +
    `<wp:extent cx="${widthEmu}" cy="${heightEmu}"/>` +
    '<wp:effectExtent l="0" t="0" r="0" b="0"/>' +
    `<wp:docPr id="${docPrId}" name="Artefact ${docPrId}"/>` +
    '<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">' +
    '<a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">' +
    '<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">' +
    "<pic:nvPicPr>" +
    `<pic:cNvPr id="${docPrId}" name="artefact_${docPrId}.png"/>` +
    "<pic:cNvPicPr/>" +
    "</pic:nvPicPr>" +
    "<pic:blipFill>" +
    `<a:blip r:embed="${rId}"/>` +
    "<a:stretch><a:fillRect/></a:stretch>" +
    "</pic:blipFill>" +
    "<pic:spPr>" +
    "<a:xfrm>" +
    '<a:off x="0" y="0"/>' +
    `<a:ext cx="${widthEmu}" cy="${heightEmu}"/>` +
    "</a:xfrm>" +
    '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom>' +
    "</pic:spPr>" +
    "</pic:pic>" +
    "</a:graphicData>" +
    "</a:graphic>" +
    "</wp:inline>" +
    "</w:drawing>" +
    "</w:r>" +
    "</w:p>"
  );
}

/**
 * Build OOXML for a table with borders, bold header row, and auto-width columns.
 */
function buildTableXml(headers: string[], rows: Array<Record<string, unknown>>): string {
  const borderDef =
    '<w:top w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>' +
    '<w:left w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>' +
    '<w:bottom w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>' +
    '<w:right w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>' +
    '<w:insideH w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>' +
    '<w:insideV w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>';

  let xml =
    "<w:tbl>" +
    "<w:tblPr>" +
    '<w:tblW w:w="5000" w:type="pct"/>' +
    `<w:tblBorders>${borderDef}</w:tblBorders>` +
    '<w:tblCellMar><w:top w:w="40" w:type="dxa"/><w:left w:w="80" w:type="dxa"/>' +
    '<w:bottom w:w="40" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar>' +
    "</w:tblPr>";

  // Header row — bold text, light grey background
  xml += "<w:tr>";

  for (const header of headers) {
    xml +=
      "<w:tc>" +
      '<w:tcPr><w:shd w:val="clear" w:color="auto" w:fill="F2F2F2"/></w:tcPr>' +
      "<w:p><w:r><w:rPr><w:b/></w:rPr>" +
      `<w:t xml:space="preserve">${escapeXml(String(header))}</w:t>` +
      "</w:r></w:p>" +
      "</w:tc>";
  }

  xml += "</w:tr>";

  // Data rows
  for (const row of rows) {
    xml += "<w:tr>";

    for (const header of headers) {
      const value = row[header] ?? "";

      xml +=
        "<w:tc>" +
        "<w:p><w:r>" +
        `<w:t xml:space="preserve">${escapeXml(String(value))}</w:t>` +
        "</w:r></w:p>" +
        "</w:tc>";
    }

    xml += "</w:tr>";
  }

  xml += "</w:tbl>";

  return xml;
}

/**
 * Build OOXML for a Table of Contents field code.
 *
 * Word evaluates the TOC field when the document is opened and generates
 * entries from heading-styled paragraphs. The placeholder text tells the
 * user to right-click → "Update Field" to populate it.
 */
function buildTocFieldXml(): string {
  return (
    "<w:sdt>" +
    "<w:sdtPr>" +
    "<w:docPartObj>" +
    '<w:docPartGallery w:val="Table of Contents"/>' +
    "<w:docPartUnique/>" +
    "</w:docPartObj>" +
    "</w:sdtPr>" +
    "<w:sdtContent>" +
    "<w:p>" +
    "<w:pPr><w:pStyle w:val='TOCHeading'/></w:pPr>" +
    "<w:r><w:t>Contents</w:t></w:r>" +
    "</w:p>" +
    "<w:p>" +
    '<w:r><w:fldChar w:fldCharType="begin"/></w:r>' +
    '<w:r><w:instrText xml:space="preserve"> TOC \\o "1-3" \\h \\z \\u </w:instrText></w:r>' +
    '<w:r><w:fldChar w:fldCharType="separate"/></w:r>' +
    "<w:r><w:rPr><w:i/><w:color w:val='808080'/></w:rPr>" +
    "<w:t>Right-click and select Update Field to generate table of contents</w:t></w:r>" +
    '<w:r><w:fldChar w:fldCharType="end"/></w:r>' +
    "</w:p>" +
    "</w:sdtContent>" +
    "</w:sdt>"
  );
}

/** Escape XML special characters in text content. */
function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
